import type {
  CreateIdeaInput,
  CreateProductionItemInput,
  GenerateScriptResult,
  Idea,
  KanbanBoard,
  MoveProductionItemInput,
  ProductionItem,
  StageViolation,
  UpdateProductionItemInput,
} from '@qlk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api, goiApi, type LoiApi } from '../../lib/api.js';
import { useLich } from '../schedule/api.js';

const GOC = '/api/v1/production';

export interface BoLocBang extends Record<string, string | undefined> {
  channelId?: string;
  assigneeId?: string;
}

export const KHOA = {
  bang: (loc: BoLocBang) => ['production', 'board', loc] as const,
  item: (id: string) => ['production', 'item', id] as const,
  yTuong: ['production', 'ideas'] as const,
};

function thamSo(doi: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [khoa, giaTri] of Object.entries(doi)) {
    if (giaTri !== undefined && giaTri !== '') sp.set(khoa, String(giaTri));
  }
  const chuoi = sp.toString();
  return chuoi ? `?${chuoi}` : '';
}

/**
 * Ngày đăng thật của từng thẻ sản xuất, khoá theo `productionItem.id`.
 *
 * Thẻ ở cột "Đã lên lịch" và "Đã đăng" phải hiện **đúng ngày trên lịch**, không
 * phải `dueDate` (hạn làm xong, thường lệch vài ngày). Ngày đăng do module 06
 * giữ, nên đọc qua endpoint lịch của module đó chứ không thêm cột vào DTO của 05.
 *
 * Lấy hai tháng (tháng này và tháng sau) vì bài lên lịch có thể vắt qua đầu
 * tháng. Thiếu quyền `schedule:view` thì không gọi gì, thẻ quay về dùng `dueDate`.
 */
export function useNgayDangTheoMuc(bat: boolean): Map<string, string> {
  const homNay = new Date();
  const thangNay = homNay.toISOString().slice(0, 10);
  const sauMotThang = new Date(Date.UTC(homNay.getUTCFullYear(), homNay.getUTCMonth() + 1, 1))
    .toISOString()
    .slice(0, 10);

  const nay = useLich({ view: 'month', anchor: thangNay }, bat);
  const sau = useLich({ view: 'month', anchor: sauMotThang }, bat);

  return useMemo(() => {
    const ra = new Map<string, string>();
    for (const lich of [nay.data, sau.data]) {
      for (const ngay of lich?.days ?? []) {
        for (const bai of ngay.posts) ra.set(bai.productionItemId, bai.publishAt);
      }
    }
    return ra;
  }, [nay.data, sau.data]);
}

/* -------------------------------- Đọc -------------------------------- */

export function useBangKanban(loc: BoLocBang) {
  return useQuery<KanbanBoard, LoiApi>({
    queryKey: KHOA.bang(loc),
    queryFn: () => api.get<KanbanBoard>(`${GOC}/board${thamSo(loc)}`),
  });
}

export function useItem(id: string | undefined) {
  return useQuery<ProductionItem, LoiApi>({
    queryKey: KHOA.item(id ?? ''),
    queryFn: () => api.get<ProductionItem>(`${GOC}/items/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useYTuong(batDau = true) {
  return useQuery<Idea[], LoiApi>({
    queryKey: KHOA.yTuong,
    queryFn: () => api.get<Idea[]>(`${GOC}/y-tuong`),
    enabled: batDau,
  });
}

/* ------------------------------ Mutation ----------------------------- */

function lamMoiBang(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ['production', 'board'] });
}

export function useTaoItem() {
  const qc = useQueryClient();
  return useMutation<ProductionItem, LoiApi, CreateProductionItemInput>({
    mutationFn: (input) => api.post<ProductionItem>(`${GOC}/items`, input),
    onSuccess: () => lamMoiBang(qc),
  });
}

export function useCapNhatItem(id: string) {
  const qc = useQueryClient();
  return useMutation<ProductionItem, LoiApi, UpdateProductionItemInput>({
    mutationFn: (input) =>
      goiApi<ProductionItem>(`${GOC}/items/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: async (du) => {
      qc.setQueryData(KHOA.item(id), du);
      await lamMoiBang(qc);
    },
  });
}

export interface LoiDiChuyen {
  violations: StageViolation[];
}

export function useDiChuyenItem() {
  const qc = useQueryClient();
  return useMutation<ProductionItem, LoiApi, { id: string } & MoveProductionItemInput>({
    mutationFn: ({ id, ...than }) => api.post<ProductionItem>(`${GOC}/items/${id}/di-chuyen`, than),
    onSettled: () => lamMoiBang(qc),
  });
}

export function useDatChecklist(id: string) {
  const qc = useQueryClient();
  return useMutation<ProductionItem['checklist'], LoiApi, { key: string; checked: boolean }>({
    mutationFn: (input) =>
      goiApi<ProductionItem['checklist']>(`${GOC}/items/${id}/checklist`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.item(id) });
      await lamMoiBang(qc);
    },
  });
}

export function useSinhKichBan(id: string) {
  const qc = useQueryClient();
  return useMutation<GenerateScriptResult, LoiApi, { prompt?: string }>({
    mutationFn: (input) => api.post<GenerateScriptResult>(`${GOC}/items/${id}/kich-ban`, input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.item(id) });
    },
  });
}

export function useTaoYTuong() {
  const qc = useQueryClient();
  return useMutation<Idea, LoiApi, CreateIdeaInput>({
    mutationFn: (input) => api.post<Idea>(`${GOC}/y-tuong`, input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.yTuong });
    },
  });
}

/* ------------------------------ Tải file ----------------------------- */

export interface TienTrinhTai {
  daGui: number;
  tong: number;
}

/**
 * Tải file lên bằng XMLHttpRequest để có thanh tiến trình (fetch chưa báo được
 * tiến trình upload). Thân request là chính đối tượng `File`, kiểu
 * `application/octet-stream` — đúng với endpoint của module 05.
 */
export function taiFileLen(
  itemId: string,
  kind: 'video' | 'thumbnail',
  file: File,
  onTienTrinh?: (t: TienTrinhTai) => void,
): { promise: Promise<{ url: string }>; huy: () => void } {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<{ url: string }>((resolve, reject) => {
    xhr.open('POST', `${GOC}/items/${itemId}/file${thamSo({ kind, filename: file.name })}`, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onTienTrinh?.({ daGui: e.loaded, tong: e.total });
    };

    xhr.onload = () => {
      let than: unknown;
      try {
        than = JSON.parse(xhr.responseText);
      } catch {
        than = null;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(than as { url: string });
        return;
      }

      const loi = (than as { error?: { message?: string } } | null)?.error;
      reject(new Error(loi?.message ?? `Tải file thất bại (${String(xhr.status)})`));
    };

    xhr.onerror = () => {
      reject(new Error('Không gửi được file lên máy chủ'));
    };
    xhr.onabort = () => {
      reject(new Error('Đã huỷ tải file'));
    };

    xhr.send(file);
  });

  return {
    promise,
    huy: () => {
      xhr.abort();
    },
  };
}
